import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Check, Search, X } from "lucide-react";
import { useDebounceValue } from "usehooks-ts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useForwardChatMessageMutation,
  useGetChatConversationsInfiniteQuery,
} from "@/services/(my-space)/api.chat";
import type { IChatConversation, IChatMessage } from "@/types/(my-space)/chat.interface";
import { cn } from "@/utils/cn";
import { getErrorMessage } from "@/utils/error.utils";
import { ChatAvatar } from "./ChatAvatar";
import { conversationTitle, messageContentPreview } from "../_lib/chat-labels";
import { MAX_FORWARD_TARGETS, canForwardTo, toggleForwardTarget } from "../_lib/forward";

interface ForwardDialogProps {
  /** The message to forward; null = closed. */
  message: IChatMessage | null;
  onClose: () => void;
}

/** Pick up to five chats and send them a copy (all or none). */
export const ForwardDialog = ({ message, onClose }: ForwardDialogProps) => {
  const { t } = useTranslation("chat");
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [debounced] = useDebounceValue(search, 300);
  const [forward, { isLoading }] = useForwardChatMessageMutation();

  // A new message starts a new pick.
  useEffect(() => {
    setSelected([]);
    setSearch("");
  }, [message?.id]);

  const query = useGetChatConversationsInfiniteQuery(
    { tab: "all", archived: false, search: debounced },
    { skip: !message },
  );
  const chats = useMemo(
    () =>
      (query.data?.pages.flatMap((p) => p.data) ?? []).filter((c) =>
        message ? canForwardTo(c, message.type) : false,
      ),
    [message, query.data],
  );
  // Names of picked chats stay known while a search hides them.
  const known = useRef(new Map<number, IChatConversation>());
  chats.forEach((c) => known.current.set(c.id, c));

  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const full = selected.length >= MAX_FORWARD_TARGETS;
  const send = async () => {
    if (!message || !selected.length) return;
    try {
      await forward({ id: message.id, conversationIds: selected }).unwrap();
      toast.success(t("forward.sent", { count: selected.length }));
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.forward")));
    }
  };

  const preview = message
    ? messageContentPreview(t, {
        type: message.type,
        body: message.body,
        metadata: message.metadata,
        attachmentCount: message.attachments.length,
        isDeleted: message.isDeleted,
      })
    : "";

  return (
    <Dialog open={!!message} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex h-[min(680px,90vh)] flex-col gap-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("forward.title")}</DialogTitle>
          <DialogDescription dir="auto" className="truncate">
            {preview}
          </DialogDescription>
        </DialogHeader>

        <div className="relative shrink-0">
          <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("forward.search")}
            aria-label={t("forward.search")}
            className="h-10 ps-9"
            autoFocus
          />
        </div>

        {selected.length ? (
          <div className="flex shrink-0 flex-wrap gap-1.5">
            {selected.map((id) => {
              const c = known.current.get(id);
              const name = c ? conversationTitle(t, c) : "";
              return (
                <span
                  key={id}
                  className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 py-0.5 ps-2.5 pe-1 text-xs font-medium text-primary"
                >
                  <span className="truncate">{name}</span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="size-5 rounded-full"
                    onClick={() => setSelected((s) => s.filter((x) => x !== id))}
                    aria-label={t("forward.remove", { name })}
                  >
                    <X className="size-3" />
                  </Button>
                </span>
              );
            })}
          </div>
        ) : null}

        <div className="-mx-2 min-h-0 flex-1 overflow-y-auto">
          {query.isLoading ? (
            <div className="flex flex-col gap-2 px-2 py-1">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                  <Skeleton className="size-9 rounded-full" />
                  <Skeleton className="h-3.5 w-1/2" />
                </div>
              ))}
            </div>
          ) : chats.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("forward.no_chats")}</p>
          ) : (
            <ul className="flex flex-col px-2">
              {chats.map((c) => {
                const isSelected = selected.includes(c.id);
                const disabled = !isSelected && full;
                const title = conversationTitle(t, c);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setSelected((s) => toggleForwardTarget(s, c.id))}
                      aria-pressed={isSelected}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-2 py-2 text-start hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
                        isSelected && "bg-primary/5",
                      )}
                    >
                      <ChatAvatar
                        name={title}
                        imageUrl={c.type === "direct" ? c.peer?.profileImage : c.imageUrl}
                        kind={c.type}
                        size="md"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {title}
                      </span>
                      <span
                        className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded-full border",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border-strong",
                        )}
                      >
                        {isSelected ? <Check className="size-3" /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
              <div ref={sentinelRef} className="h-1" />
            </ul>
          )}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {full
              ? t("forward.limit", { count: MAX_FORWARD_TARGETS })
              : t("forward.selected", { count: selected.length })}
          </span>
          <Button variant="primary" onClick={send} disabled={!selected.length} isLoading={isLoading}>
            {t("forward.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
